// Layout definitions for the DOLE form editors.
//
// `field` names are the API contract with backend/peso_reports.py builders and the
// Word/Excel renderers — never rename on one side alone.
//
// docTitle  : the bold form-title lines under the official DOLE header block
// footer    : which signatory shape the form uses —
//               'certified' = "CERTIFIED CORRECT:" + certified_by
//               'lmi'       = "Reported by:" + "Certified by:" (and the certificate no.)
//               'sprs'      = "Prepared by:" + "Noted by:" (the Municipal Mayor)
// fixedRows : suppresses row add/delete for fixed-template forms (LMI Form 5)

export const REPORT_DEFS = {
  applicants_registered: {
    title: 'Applicants Registered',
    docTitle: ['APPLICANTS REGISTERED'],
    footer: 'certified',
    columns: [
      { field: 'name', label: 'Name of Applicant (Last Name, First, MI)', type: 'text', width: 'min-w-[180px]' },
      { field: 'sex', label: 'Gender', type: 'mf' },
      { field: 'address', label: 'Address', type: 'text', width: 'min-w-[150px]' },
      { field: 'phone', label: 'Telephone/cellphone no.', type: 'text' },
      { field: 'assist_seeking', label: 'Seeking Employment', type: 'check' },
      { field: 'assist_counseling', label: 'Counseling', type: 'check' },
      { field: 'assist_others', label: 'Others', type: 'text' },
    ],
    addRowEnabled: true,
  },
  vacancy_solicitation: {
    title: 'Vacancy Solicitation Report',
    docTitle: ['VACANCY SOLICITATION REPORT'],
    footer: 'certified',
    columns: [
      { field: 'company', label: 'Company Name', type: 'text', width: 'min-w-[150px]' },
      { field: 'address', label: 'Address', type: 'text' },
      { field: 'positions', label: 'Position', type: 'text', width: 'min-w-[220px]' },
      { field: 'description', label: 'Job Description', type: 'text' },
      { field: 'vacancy_count', label: 'Vacancy Count', type: 'number' },
      { field: 'remarks', label: 'Remarks', type: 'text' },
    ],
    addRowEnabled: true,
  },
  placement_summary: {
    title: 'Monthly Placement Summary Report',
    docTitle: ['MONTHLY PLACEMENT SUMMARY REPORT', 'For Local Employment'],
    footer: 'certified',
    columns: [
      { field: 'name', label: 'Name of Applicant', type: 'text', width: 'min-w-[180px]' },
      { field: 'company', label: 'Name of Company', type: 'text', width: 'min-w-[150px]' },
      { field: 'sector', label: 'Sector', type: 'select', options: ['PRIVATE', 'OVERSEAS', 'GOVERNMENT'] },
      { field: 'position', label: 'Position', type: 'text' },
      { field: 'sex', label: 'Gender', type: 'mf' },
      { field: 'date_hired', label: 'Date Hired', type: 'text' },
    ],
    addRowEnabled: true,
  },
  lmi_form1: {
    title: 'LMI Form 1 — Individuals Reached',
    docTitle: ['LABOR MARKET INFORMATION', 'MONITORING FORM 1'],
    footer: 'lmi',
    columns: [
      { field: 'name', label: 'Name', type: 'text', width: 'min-w-[170px]' },
      { field: 'address', label: 'Address', type: 'text', width: 'min-w-[170px]' },
      { field: 'contact', label: 'Contact Details', type: 'text' },
      { field: 'sex', label: 'Sex', type: 'mf' },
      // Auto from date of birth; the toggle is editable only for the rare no-DOB row.
      { field: 'client_type', label: 'Client Type (Youth ≤24 / Non-Youth 25+)', type: 'youth' },
    ],
    addRowEnabled: true,
  },
  lmi_form2: {
    title: 'LMI Form 2 — Institutions Reached',
    docTitle: ['LABOR MARKET INFORMATION', 'MONITORING FORM 2'],
    footer: 'lmi',
    columns: [
      { field: 'institution', label: 'Institution', type: 'text', width: 'min-w-[170px]' },
      { field: 'address', label: 'Address', type: 'text', width: 'min-w-[170px]' },
      { field: 'contact', label: 'Contact Details', type: 'text' },
      { field: 'lmi_type', label: 'Type of LMI Provided', type: 'text', width: 'min-w-[200px]' },
    ],
    addRowEnabled: true,
  },
  lmi_form5: {
    title: 'LMI Form 5 — New Media Reach',
    docTitle: ['LABOR MARKET INFORMATION', 'MONITORING FORM 5'],
    footer: 'lmi',
    // Fixed three-channel template; staff enter figures from their new-media reports.
    fixedRows: true,
    columns: [
      { field: 'channel', label: 'New Media', type: 'text', width: 'min-w-[160px]' },
      { field: 'reached', label: 'Individuals Reached', type: 'number' },
      { field: 'male', label: 'Male', type: 'number' },
      { field: 'female', label: 'Female', type: 'number' },
      { field: 'youth', label: 'Youth (15-24)', type: 'number' },
      { field: 'nonyouth', label: 'Non-Youth (25+)', type: 'number' },
    ],
    addRowEnabled: false,
  },
  sprs: {
    title: 'SPRS Monthly Report',
    docTitle: ['STATISTICAL AND PERFORMANCE REPORTING SYSTEM (SPRS)'],
    footer: 'sprs',
    custom: 'sprs',   // rendered by SprsEditor, not the generic table
  },
};

// All seven DOLE forms. Six use the generic table editor; SPRS (custom: 'sprs') uses
// its own tree editor.
export const AVAILABLE_TYPES = Object.keys(REPORT_DEFS);

export const REPORT_BLURBS = {
  applicants_registered: 'Jobseekers who registered with your office this month.',
  vacancy_solicitation: 'Vacancies solicited from your employers this month.',
  placement_summary: 'Your registrants hired this month.',
  lmi_form1: 'Individuals reached — registrants with youth / non-youth split.',
  lmi_form2: 'Institutions reached — employers you engaged, plus schools & GOs/NGOs.',
  lmi_form5: 'New-media reach (Facebook, email, website). Enter your own figures.',
  sprs: 'The full monthly SPRS packet. Platform figures pre-fill as editable minimums.',
};
